import * as React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { getVarietyBetRestrictions, updateVarietyBetRestrictions } from '../Api';
import VarietyBetRestrictionsDto, {
    VarietyBetRestrictionRuleDto,
} from '../models/dtos/VarietyBetRestrictionsDto';

const PLAYER_STATS_RULE_ID = 'player_stats';
const PLAYER_STATS_PARAM_T_ID = 11;

function playerStatsRule(): VarietyBetRestrictionRuleDto {
    return {
        id: PLAYER_STATS_RULE_ID,
        enabled: true,
        paramTIds: [PLAYER_STATS_PARAM_T_ID],
        minStakeBetcoins: 1000,
        enforcementEnabled: true,
        visibility: { standardBets: true, challenge: true, competition: true },
    };
}

function pickPlayerStatsRule(rules: VarietyBetRestrictionRuleDto[] | undefined): VarietyBetRestrictionRuleDto {
    const found = rules?.find(r => r.id === PLAYER_STATS_RULE_ID);
    if (found == null) return playerStatsRule();
    return {
        ...found,
        paramTIds: found.paramTIds?.length ? found.paramTIds : [PLAYER_STATS_PARAM_T_ID],
    };
}

export default function VarietyBetRestrictionsPage() {
    const [loading, setLoading] = React.useState(true);
    const [globalEnabled, setGlobalEnabled] = React.useState(true);
    const [ruleEnabled, setRuleEnabled] = React.useState(true);
    const [enforcementEnabled, setEnforcementEnabled] = React.useState(true);
    const [minStakeBetcoins, setMinStakeBetcoins] = React.useState(1000);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');

    const applyDto = (dto: VarietyBetRestrictionsDto | null) => {
        if (dto == null) return;
        setGlobalEnabled(dto.globalEnabled ?? true);
        const rule = pickPlayerStatsRule(dto.rules);
        setRuleEnabled(rule.enabled);
        setEnforcementEnabled(rule.enforcementEnabled);
        setMinStakeBetcoins(rule.minStakeBetcoins);
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const dto = await getVarietyBetRestrictions();
            if (!cancelled) {
                applyDto(dto);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const openDialog = (title: string, message: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogOpen(true);
    };

    const onSave = async () => {
        if (minStakeBetcoins < 0) {
            openDialog('Error', 'Min stake must be non-negative.');
            return;
        }
        const payload: VarietyBetRestrictionsDto = {
            version: 1,
            globalEnabled,
            rules: [
                {
                    ...playerStatsRule(),
                    enabled: ruleEnabled,
                    enforcementEnabled,
                    minStakeBetcoins,
                },
            ],
        };
        const saveError = await updateVarietyBetRestrictions(payload);
        if (saveError != null) {
            openDialog('Error', saveError);
            return;
        }
        const dto = await getVarietyBetRestrictions();
        applyDto(dto);
        openDialog('Saved', 'Player Stats variety restrictions were saved.');
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">Player Stats — min stake</Typography>
            <Typography variant="body2" color="text.secondary">
                Settings for the <strong>Player Stats</strong> variety tab (game type id {PLAYER_STATS_PARAM_T_ID} /
                paramTId). Applies to all bet groups on that tab. Stored in variety_bet_restrictions.config. App
                version filtering remains on Variety version gates.
            </Typography>
            {loading ? (
                <Box><CircularProgress /></Box>
            ) : (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={globalEnabled}
                                onChange={e => setGlobalEnabled(e.target.checked)}
                            />
                        }
                        label="Global enabled"
                    />
                    <Grid container spacing={2} sx={{ maxWidth: 480 }}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={ruleEnabled}
                                        onChange={e => setRuleEnabled(e.target.checked)}
                                    />
                                }
                                label="Player Stats rule enabled"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={enforcementEnabled}
                                        onChange={e => setEnforcementEnabled(e.target.checked)}
                                    />
                                }
                                label="Enforce min stake"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Min stake (betcoins)"
                                type="number"
                                fullWidth
                                value={minStakeBetcoins}
                                onChange={e => setMinStakeBetcoins(parseInt(e.target.value, 10) || 0)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                    </Grid>
                    <Box>
                        <Button variant="contained" onClick={onSave}>Save</Button>
                    </Box>
                </>
            )}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" component="div">{dialogMsg}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
